import type { CatalogRow, ColumnMapping, PricingRules, RawRow, RowIssue } from './catalog'
import { calculateRegularPrice, parseMoneyToCents } from './pricing'

const getText = (row: RawRow, header: string): string => {
  if (!header) return ''
  const value = row[header]
  return value === null || value === undefined ? '' : String(value).trim()
}

const parseStock = (value: string): number | null => {
  if (!value) return null
  const parsed = Number(value.replace(',', '.'))
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null
}

const isValidHttpUrl = (value: string): boolean => {
  if (!value) return true
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function buildCatalogRows(
  rawRows: RawRow[],
  mapping: ColumnMapping,
  pricing: PricingRules,
): CatalogRow[] {
  const rows = rawRows.map((rawRow, index): CatalogRow => {
    const issues: RowIssue[] = []
    const sku = getText(rawRow, mapping.sku)
    const name = getText(rawRow, mapping.name)
    const priceText = getText(rawRow, mapping.price)
    const supplierPriceCents = parseMoneyToCents(priceText)
    const stock = mapping.stock ? parseStock(getText(rawRow, mapping.stock)) : null
    const category = getText(rawRow, mapping.category)
    const imageUrl = getText(rawRow, mapping.image)

    if (!sku) issues.push({ code: 'missing_sku', severity: 'error', message: 'SKU is required.' })
    if (!name) issues.push({ code: 'missing_name', severity: 'error', message: 'Product name is required.' })
    if (supplierPriceCents === null || supplierPriceCents < 0) {
      issues.push({ code: 'invalid_price', severity: 'error', message: 'Price is missing or invalid.' })
    }
    if (mapping.stock && (stock === null || stock < 0)) {
      issues.push({ code: 'invalid_stock', severity: 'error', message: 'Stock is missing or invalid.' })
    }
    if (!isValidHttpUrl(imageUrl)) {
      issues.push({ code: 'invalid_image_url', severity: 'warning', message: 'Image URL is not a valid HTTP(S) URL.' })
    }

    return {
      sourceRowNumber: index + 2,
      sku,
      name,
      supplierPriceCents,
      regularPriceCents:
        supplierPriceCents !== null && supplierPriceCents >= 0
          ? calculateRegularPrice(supplierPriceCents, pricing)
          : null,
      stock,
      category,
      imageUrl,
      status: issues.some((issue) => issue.severity === 'error')
        ? 'excluded'
        : issues.length
          ? 'warning'
          : 'valid',
      issues,
    }
  })

  const skuCounts = new Map<string, number>()
  for (const row of rows) {
    const normalizedSku = row.sku.trim().toLocaleLowerCase()
    if (normalizedSku) skuCounts.set(normalizedSku, (skuCounts.get(normalizedSku) ?? 0) + 1)
  }

  return rows.map((row) => {
    const normalizedSku = row.sku.trim().toLocaleLowerCase()
    if (!normalizedSku || (skuCounts.get(normalizedSku) ?? 0) < 2) return row
    const issues = [
      ...row.issues,
      { code: 'duplicate_sku', severity: 'error', message: 'SKU appears more than once.' } as RowIssue,
    ]
    return { ...row, issues, status: 'excluded' }
  })
}
